import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === '/';
  // Prefix "#section" with "/" when not on home page so the browser navigates home first
  const hashTo = (hash) => (onHome ? hash : `/${hash}`);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: hashTo('#services'), label: 'Services' },
    { href: hashTo('#realisations'), label: 'Réalisations' },
    { href: hashTo('#zones'), label: 'Zones' },
    { href: hashTo('#avis'), label: 'Avis' },
    { href: hashTo('#faq'), label: 'FAQ' },
    { href: '/blog', label: 'Blog' },
    { href: hashTo('#reservation'), label: 'Devis' },
    { href: hashTo('#contact'), label: 'Contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass-effect shadow-lg py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-20 sm:h-24 w-auto transition-transform duration-300 group-hover:scale-105 drop-shadow-md"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-gray-700 font-medium hover:text-[hsl(207,90%,54%)] transition-colors duration-200 relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-0.5 after:bg-[hsl(32,95%,55%)] after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-gray-600 hover:text-[hsl(207,90%,54%)] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">{companyInfo.phone}</span>
            </a>
            <Button
              asChild
              className="bg-[hsl(32,95%,55%)] hover:bg-[hsl(32,95%,45%)] text-white font-semibold px-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <a href={hashTo('#reservation')}>Demander un devis</a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-[hsl(207,90%,54%)] transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in-up">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 font-medium hover:text-[hsl(207,90%,54%)] transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-gray-600 py-2"
              >
                <Phone className="w-4 h-4" />
                <span>{companyInfo.phone}</span>
              </a>
              <Button
                asChild
                className="bg-[hsl(32,95%,55%)] hover:bg-[hsl(32,95%,45%)] text-white font-semibold w-full"
              >
                <a href={hashTo('#reservation')} onClick={() => setIsMobileMenuOpen(false)}>
                  Demander un devis
                </a>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
